import { useCallback } from "react";
import { useDispatch, useStore } from "react-redux";
import { RootState, useAppSelector } from "src/store";
import { useSurveysApi } from "../api";
import { resetError, resetFilter, resetList, setError, setFilter, setList, setLoading } from "../slices";
import { SurveySearchSortType } from "../../campaigns/types/SurveySearchSortType";

const getServerSortField = (sort?: string): SurveySearchSortType => {
  if (!sort) return 'date_created_desc';

  const sortMap: Record<string, string> = {
    createdAt: "date_created",
    updatedAt: "date_updated",
    lastResponseAt: "last_activity",
    numberOfQuestions: "questions",
    numberOfContacts: "contacts",
    numberOfRespondedContacts: "responded_contacts",
    totalReplies: "total_replies",
  };

  const lastUnderscoreIndex = sort.lastIndexOf("_");
  const field = sort.slice(0, lastUnderscoreIndex);
  const order = sort.slice(lastUnderscoreIndex + 1);
  const serverField = sortMap[field] || field;
  const result = `${serverField}_${order}`;

  // Validate that result is a valid SurveySearchSortType
  const validSortTypes: SurveySearchSortType[] = [
    'name_asc', 'name_desc', 'status_asc', 'status_desc',
    'date_created_asc', 'date_created_desc', 'date_updated_asc', 'date_updated_desc',
    'last_activity_asc', 'last_activity_desc',
    'questions_asc', 'questions_desc', 'contacts_asc', 'contacts_desc',
    'responded_contacts_asc', 'responded_contacts_desc',
    'total_replies_asc', 'total_replies_desc',
  ];

  return validSortTypes.includes(result as SurveySearchSortType) ? result as SurveySearchSortType : 'date_updated_desc';
};

export const useSurveysService = () => {
  const dispatch = useDispatch();
  const { getState } = useStore<RootState>();
  const { loading, filter, list, error } = useAppSelector((state) => state.surveyResponses);

  const api = useSurveysApi();

  const getSurveys = useCallback(async (companyId: string) => {
    const filter = getState().surveyResponses.filter;

    try {
      dispatch(resetError());
      dispatch(setLoading({ getList: true }));

      const response = await api.searchSurveys({
        companyId,
        sort: getServerSortField(filter.sort),
        offset: filter.offset,
        limit: filter.limit,
        query: filter.query || undefined,
        status: filter.status || undefined,
        startDateInterval: filter.startDateInterval || undefined,
        endDateInterval: filter.endDateInterval || undefined,
      });
      dispatch(setList(response.data));

      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getList: false }));
    }
  }, []);

  const changeSurveysFilter = useCallback((filter: Partial<RootState["surveyResponses"]["filter"]>) => {
    dispatch(setFilter(filter));
  }, []);

  const resetSurveys = useCallback(() => {
    dispatch(resetList());
  }, []);

  const resetSurveysFilter = useCallback(() => {
    dispatch(resetFilter());
  }, []);

  return {
    loading,
    filter,
    list,
    error,
    getSurveys,
    changeSurveysFilter,
    resetSurveys,
    resetSurveysFilter,
  };
};
